import { definePreset } from '@primeng/themes';
import Material from '@primeng/themes/material';

/** md-light-indigo / develop staging */
const colorLabel = '#009688';
const colorTextPrimary = '#000000';
const colorTextSelected = '#3f51b5';
const colorBgSelected = '#e8eaf6';
const colorBgHover = '#f5f5f5';
const colorBorderUnderline = 'rgba(0, 0, 0, 0.38)';
const colorIconMuted = 'rgba(0, 0, 0, 0.54)';

const selectOverlayLight = {
  background: '#ffffff',
  color: colorTextPrimary,
  borderColor: 'rgba(0, 0, 0, 0.12)'
};

const selectListOptionLight = {
  focusBackground: colorBgHover,
  focusColor: colorTextPrimary,
  selectedBackground: colorBgSelected,
  selectedFocusBackground: colorBgSelected,
  color: colorTextPrimary,
  selectedColor: colorTextSelected,
  selectedFocusColor: colorTextSelected
};

/**
 * Staging-aligned preset (develop md-light-indigo underline forms).
 * See summaries/prework_primeng_migrations_p2_results.md
 */
export const XiroccoPreset = definePreset(Material, {
  semantic: {
    colorScheme: {
      light: {
        overlay: { select: selectOverlayLight },
        list: { option: selectListOptionLight }
      },
      dark: {
        overlay: { select: selectOverlayLight },
        list: { option: selectListOptionLight }
      }
    }
  },
  components: {
    floatlabel: {
      root: {
        fontWeight: '500',
        color: colorLabel,
        focusColor: colorLabel,
        activeColor: colorLabel,
        positionX: '0',
        active: {
          fontSize: '15px',
          fontWeight: '500'
        }
      },
      over: {
        active: {
          top: '-0.5rem'
        }
      },
      on: {
        active: {
          background: 'transparent',
          padding: '0'
        }
      },
      css: `
        .p-floatlabel label {
          font-family: Roboto, sans-serif;
          font-size: 15px;
          font-weight: 500;
          line-height: 15px;
        }
        .p-floatlabel:has(.p-inputwrapper-filled) label,
        .p-floatlabel:has(.p-inputwrapper-focus) label,
        .p-floatlabel:has(input.p-filled) label,
        .p-floatlabel:has(input:focus) label,
        .p-floatlabel:has(textarea.p-filled) label,
        .p-floatlabel:has(textarea:focus) label {
          font-size: 15px !important;
          font-weight: 500 !important;
          line-height: 15px !important;
          top: -0.5rem !important;
          transform: none !important;
          background-color: #ffffff;
          padding: 2px 4px !important;
          margin-left: -4px;
        }
      `
    },
    inputtext: {
      root: {
        background: 'transparent',
        disabledBackground: 'transparent',
        filledBackground: 'transparent',
        filledHoverBackground: 'transparent',
        filledFocusBackground: 'transparent',
        borderColor: colorBorderUnderline,
        hoverBorderColor: colorBorderUnderline,
        focusBorderColor: colorLabel,
        color: colorTextPrimary,
        shadow: 'none',
        borderRadius: '0',
        paddingX: '0',
        focusRing: { width: '0', shadow: 'none' }
      },
      css: `
        .form-floating .p-inputtext,
        .accordion-body .p-inputtext {
          font-family: Roboto, sans-serif;
          font-size: 15px;
          background: transparent;
          background-image: none;
          border-width: 0 0 1px 0;
          border-color: ${colorBorderUnderline};
          box-shadow: none;
        }
      `
    },
    password: {
      icon: { color: colorLabel }
    },
    inputnumber: {
      root: { transitionDuration: '0.2s' },
      button: {
        background: 'transparent',
        hoverBackground: 'rgba(0, 0, 0, 0.04)',
        activeBackground: 'rgba(0, 0, 0, 0.08)',
        borderColor: 'transparent',
        hoverBorderColor: 'transparent',
        activeBorderColor: 'transparent',
        color: colorIconMuted,
        hoverColor: colorIconMuted,
        activeColor: colorIconMuted
      },
      css: `
        .p-inputnumber .p-inputnumber-input {
          font-family: Roboto, sans-serif;
          font-size: 15px;
          background: transparent;
          background-image: none;
          box-shadow: none;
        }
        .p-inputnumber.p-variant-filled {
          background: transparent;
        }
      `
    },
    select: {
      root: {
        background: 'transparent',
        filledBackground: 'transparent',
        filledHoverBackground: 'transparent',
        filledFocusBackground: 'transparent',
        borderColor: colorBorderUnderline,
        hoverBorderColor: colorBorderUnderline,
        focusBorderColor: colorBorderUnderline,
        color: colorTextPrimary,
        shadow: 'none',
        borderRadius: '0',
        paddingX: '0',
        paddingY: '0',
        focusRing: { width: '0', shadow: 'none' }
      },
      dropdown: { color: colorIconMuted, width: '2.357rem' },
      overlay: {
        background: '#ffffff',
        color: colorTextPrimary,
        borderColor: 'rgba(0, 0, 0, 0.12)',
        shadow:
          '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)'
      },
      list: { padding: '0', gap: '0' },
      option: {
        padding: 'calc(0.5rem + 2px) 1rem',
        focusBackground: colorBgHover,
        focusColor: colorTextPrimary,
        selectedBackground: colorBgSelected,
        selectedFocusBackground: colorBgSelected,
        color: colorTextPrimary,
        selectedColor: colorTextSelected,
        selectedFocusColor: colorTextSelected
      },
      css: `
        .p-select .p-select-label {
          font-family: Roboto, sans-serif;
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
        }
        .p-select-overlay .p-select-list {
          gap: 0;
        }
        .p-select-overlay .p-select-list-container {
          max-height: 300px;
          overflow-y: auto;
        }
        .p-select-overlay .p-select-option {
          font-family: Roboto, sans-serif;
          font-size: 16px;
          font-weight: 400;
          line-height: normal;
        }
      `
    },
    datepicker: {
      root: { transitionDuration: '0.2s' },
      panel: {
        background: '#ffffff',
        color: colorTextPrimary,
        borderColor: 'rgba(0, 0, 0, 0.12)',
        shadow:
          '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)'
      },
      dropdown: {
        background: 'transparent',
        hoverBackground: 'transparent',
        activeBackground: 'transparent',
        borderColor: 'transparent',
        hoverBorderColor: 'transparent',
        activeBorderColor: 'transparent',
        color: colorIconMuted,
        hoverColor: colorIconMuted,
        activeColor: colorIconMuted,
        borderRadius: '0',
        focusRing: { width: '0', shadow: 'none' }
      },
      inputIcon: { color: colorIconMuted },
      date: {
        selectedBackground: colorBgSelected,
        selectedColor: colorTextSelected,
        rangeSelectedBackground: colorBgSelected,
        rangeSelectedColor: colorTextSelected,
        hoverBackground: colorBgSelected,
        hoverColor: colorTextSelected
      },
      css: `
        .p-datepicker .p-datepicker-input,
        .p-datepicker .p-datepicker-input:enabled:hover,
        .p-datepicker .p-datepicker-input:enabled:focus {
          font-family: Roboto, sans-serif;
          font-size: 15px;
          background: transparent;
          border: 0;
          border-color: transparent;
          box-shadow: none;
          outline: none;
        }
        .p-datepicker .p-datepicker-dropdown,
        .p-datepicker .p-datepicker-dropdown:not(:disabled):hover,
        .p-datepicker .p-datepicker-dropdown:not(:disabled):active {
          background: transparent;
          border: 0;
          border-color: transparent;
          box-shadow: none;
          outline: none;
        }
      `
    },
    dialog: {
      root: {
        borderRadius: '4px',
        shadow:
          '0 11px 15px -7px rgba(0, 0, 0, 0.2), 0 24px 38px 3px rgba(0, 0, 0, 0.14), 0 9px 46px 8px rgba(0, 0, 0, 0.12)'
      },
      header: { padding: '0 1.5rem', gap: '0' },
      title: { fontSize: '1.25rem', fontWeight: '500' },
      content: { padding: '1.5rem' },
      footer: { padding: '1rem 1.5rem' },
      css: `
        .p-dialog .p-dialog-header {
          background: #62bcc3;
          color: #fff;
          height: 60px;
          min-height: 60px;
          max-height: 60px;
          padding: 0 1.5rem;
          box-sizing: border-box;
        }
        .p-dialog .p-dialog-title {
          color: #fff;
          font-family: Roboto, sans-serif;
          line-height: normal;
        }
        .p-dialog .p-dialog-footer {
          display: block;
          text-align: right;
        }
      `
    },
    confirmdialog: {
      icon: {
        size: '2rem',
        color: 'rgba(0, 0, 0, 0.87)'
      },
      content: { gap: '0' }
    },
    toast: {
      root: { width: '25rem' },
      summary: {
        fontSize: '15px',
        fontWeight: '500'
      },
      detail: {
        fontSize: '15px',
        fontWeight: '400'
      },
      css: `
        .p-toast .p-toast-summary,
        .p-toast .p-toast-detail {
          font-family: Roboto, sans-serif;
          line-height: normal;
        }
      `
    }
  }
});
